import { useState, useCallback, useEffect, useMemo } from 'react';
import { saveMapStyleFromMapToStorage } from '../utils/mapStylePersistence';

/**
 * Custom hook for managing road width adjustments
 */
const useRoadWidth = (mapRef) => {
  // Road type categories with default widths from tegola config
  const roadTypes = useMemo(() => ({
    motorway: {
      label: 'Motorway/Trunk',
      casingLayers: ['roads_mw_trunk_low_casing', 'roads_mw_trunk_mid_casing'],
      centerLayers: ['roads_mw_trunk_low', 'roads_mw_trunk_mid'],
      defaultWidths: {
        casing: {
          5: 0.26,
          6: 0.51,
          7: 1.1,
          8: 1.1,
          9: 2.1,
          10: 2.1,
          11: 6,
          12: 6,
          13: 6,
          14: 12,
          15: 14,
          16: 16,
          17: 22,
          18: 32,
          19: 72,
          20: 72
        },
        center: {
          5: 0.25,
          6: 0.5,
          7: 1,
          8: 1,
          9: 2,
          10: 2,
          11: 4,
          12: 4,
          13: 4,
          14: 10,
          15: 12,
          16: 14,
          17: 20,
          18: 30,
          19: 70,
          20: 70
        }
      }
    },
    primary: {
      label: 'Primary/Secondary',
      casingLayers: ['roads_primary_casing', 'roads_secondary_casing'],
      centerLayers: ['roads_primary', 'roads_secondary'],
      defaultWidths: {
        casing: {
          7: 0.51,
          8: 0.51,
          9: 0.51,
          10: 1.5,
          11: 1.5,
          12: 2.5,
          13: 2.5,
          14: 5,
          15: 7,
          16: 10,
          17: 12,
          18: 22,
          19: 52,
          20: 52
        },
        center: {
          7: 0.5,
          8: 0.5,
          9: 0.5,
          10: 1,
          11: 1,
          12: 2,
          13: 2,
          14: 4,
          15: 6,
          16: 8,
          17: 10,
          18: 20,
          19: 50,
          20: 50
        }
      }
    },
    tertiary: {
      label: 'Tertiary',
      casingLayers: ['roads_tertiary_mid_casing'],
      centerLayers: ['roads_tertiary_mid'],
      defaultWidths: {
        casing: {
          12: 0.51,
          13: 1.1,
          14: 2.5,
          15: 4,
          16: 5,
          17: 7,
          18: 18,
          19: 42,
          20: 42
        },
        center: {
          12: 0.5,
          13: 1,
          14: 2,
          15: 3,
          16: 4,
          17: 6,
          18: 16,
          19: 40,
          20: 40
        }
      }
    },
    residential: {
      label: 'Residential',
      casingLayers: ['roads_residential_high_casing'],
      centerLayers: ['roads_residential_high'],
      defaultWidths: {
        casing: {
          14: 0.26,
          15: 1.1,
          16: 2.1,
          17: 2.5,
          18: 14,
          19: 37,
          20: 37
        },
        center: {
          14: 0.25,
          15: 1,
          16: 2,
          17: 2,
          18: 12,
          19: 35,
          20: 35
        }
      }
    },
    service: {
      label: 'Service',
      casingLayers: ['roads_service_high_casing'],
      centerLayers: ['roads_service_high'],
      defaultWidths: {
        casing: {
          17: 2,
          18: 6,
          19: 22,
          20: 22
        },
        center: {
          17: 2,
          18: 6,
          19: 22,
          20: 22
        }
      }
    },
    paths: {
      label: 'Paths',
      casingLayers: ['roads_paths_casing'],
      centerLayers: ['roads_paths'],
      defaultWidths: {
        casing: { 6: 0, 10: 0, 14: 1, 18: 8, 20: 82 },
        center: { 6: 0, 10: 0, 14: 1, 18: 6, 20: 80 }
      }
    }
  }), []);

  const [selectedRoadType, setSelectedRoadType] = useState('motorway');
  const [currentZoom, setCurrentZoom] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const getDefaultStartZoomForType = useCallback((typeConfig) => {
    const allZooms = [
      ...Object.keys(typeConfig.defaultWidths.casing),
      ...Object.keys(typeConfig.defaultWidths.center)
    ]
      .map(Number)
      .filter(z => !Number.isNaN(z));

    return allZooms.length > 0 ? Math.min(...allZooms) : 0;
  }, []);

  const defaultStartZoomLevels = useMemo(() => (
    Object.entries(roadTypes).reduce((acc, [key, typeConfig]) => {
      acc[key] = getDefaultStartZoomForType(typeConfig);
      return acc;
    }, {})
  ), [roadTypes, getDefaultStartZoomForType]);

  // Current width values for the selected road type
  const [widthValues, setWidthValues] = useState({
    casing: { ...roadTypes.motorway.defaultWidths.casing },
    center: { ...roadTypes.motorway.defaultWidths.center }
  });

  const [startZoomLevels, setStartZoomLevels] = useState(defaultStartZoomLevels);

  const startZoomLevel = (
    startZoomLevels[selectedRoadType]
    ?? defaultStartZoomLevels[selectedRoadType]
    ?? 0
  );

  /**
   * Update width value for a specific zoom level
   */
  const updateWidth = useCallback((type, zoom, value) => {
    setWidthValues(prev => {
      const newValues = { ...prev };
      if (value === undefined || value === '' || value === null) {
        const newTypeValues = { ...newValues[type] };
        delete newTypeValues[zoom];
        newValues[type] = newTypeValues;
      } else {
        newValues[type] = {
          ...newValues[type],
          [zoom]: parseFloat(value)
        };
      }
      return newValues;
    });
  }, []);

  /**
   * Update starting zoom level for selected road type
   */
  const updateStartZoom = useCallback((value) => {
    const fallbackZoom = defaultStartZoomLevels[selectedRoadType] ?? 0;
    const parsed = parseFloat(value);
    const normalized = Number.isFinite(parsed) ? parsed : fallbackZoom;
    const clamped = Math.min(Math.max(normalized, 0), 24);

    setStartZoomLevels(prev => ({
      ...prev,
      [selectedRoadType]: clamped
    }));
  }, [selectedRoadType, defaultStartZoomLevels]);

  /**
   * Apply changes to map
   */
  const applyChanges = useCallback(() => {
    if (!mapRef?.current || !mapRef.current.isStyleLoaded()) {
      return;
    }

    const map = mapRef.current;
    const roadType = roadTypes[selectedRoadType];

    // Get current width values at the time of clicking
    setWidthValues(currentValues => {
      const currentStartZoom = (
        startZoomLevels[selectedRoadType]
        ?? defaultStartZoomLevels[selectedRoadType]
        ?? 0
      );

      // Build interpolate expression
      const buildExpression = (widths) => {
        const zooms = Object.keys(widths).map(Number).sort((a, b) => a - b);
        if (zooms.length === 0) return null;

        const expr = ['interpolate', ['linear'], ['zoom']];
        zooms.forEach(z => {
          expr.push(z);
          expr.push(parseFloat(widths[z]));
        });

        return expr;
      };

      // Apply casing
      const casingExpr = buildExpression(currentValues.casing);
      if (casingExpr && casingExpr.length > 3) {
        roadType.casingLayers.forEach(layerId => {
          if (map.getLayer(layerId)) {
            map.setPaintProperty(layerId, 'line-width', casingExpr);
            map.setPaintProperty(layerId, 'line-opacity', [
              'step',
              ['zoom'],
              0,
              currentStartZoom,
              1
            ]);
          }
        });
      }

      // Apply center
      const centerExpr = buildExpression(currentValues.center);
      if (centerExpr && centerExpr.length > 3) {
        roadType.centerLayers.forEach(layerId => {
          if (map.getLayer(layerId)) {
            map.setPaintProperty(layerId, 'line-width', centerExpr);
            map.setPaintProperty(layerId, 'line-opacity', [
              'step',
              ['zoom'],
              0,
              currentStartZoom,
              1
            ]);
          }
        });
      }

      // Force map to repaint
      map.triggerRepaint();
      saveMapStyleFromMapToStorage(map);

      return currentValues; // Return unchanged
    });
  }, [mapRef, selectedRoadType, roadTypes, startZoomLevels, defaultStartZoomLevels]);

  /**
   * Reset to defaults
   */
  const resetWidths = useCallback(() => {
    const roadType = roadTypes[selectedRoadType];
    setWidthValues({
      casing: { ...roadType.defaultWidths.casing },
      center: { ...roadType.defaultWidths.center }
    });
    setStartZoomLevels(prev => ({
      ...prev,
      [selectedRoadType]: defaultStartZoomLevels[selectedRoadType] ?? 0
    }));
  }, [selectedRoadType, roadTypes, defaultStartZoomLevels]);

  /**
   * Toggle panel
   */
  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
  }, []);

  /**
   * Load defaults when road type changes
   */
  useEffect(() => {
    const roadType = roadTypes[selectedRoadType];
    setWidthValues({
      casing: { ...roadType.defaultWidths.casing },
      center: { ...roadType.defaultWidths.center }
    });
  }, [selectedRoadType, roadTypes]);

  /**
   * Track zoom level
   */
  useEffect(() => {
    if (!mapRef?.current) return;

    const map = mapRef.current;
    const updateZoom = () => {
      setCurrentZoom(parseFloat(map.getZoom().toFixed(1)));
    };

    if (map.loaded()) {
      updateZoom();
    } else {
      map.once('load', updateZoom);
    }

    map.on('zoom', updateZoom);
    map.on('zoomend', updateZoom);

    return () => {
      map.off('zoom', updateZoom);
      map.off('zoomend', updateZoom);
    };
  }, [mapRef]);

  return {
    roadTypes,
    selectedRoadType,
    setSelectedRoadType,
    currentZoom,
    widthValues,
    startZoomLevel,
    updateWidth,
    updateStartZoom,
    applyChanges,
    resetWidths,
    isPanelOpen,
    togglePanel
  };
};

export default useRoadWidth;
